import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { X } from 'lucide-react';
import { api } from '../../lib/api';
import { SEARCH_MODULES } from './searchModules';
import { HOTEL_FILTERS } from './hotelFilters';
import SearchHeader from './SearchHeader';
import FilterSidebar from './FilterSidebar';
import SortBar from './SortBar';
import ResultsSkeleton from './ResultsSkeleton';
import HotelModifySearch from './HotelModifySearch';
import FlightModifySearch from './FlightModifySearch';
import { HotelMapSidebar } from './HotelMap';
import { RecentlyViewedHotels } from '../discovery/RecentRails';

/**
 * Generic OTA-grade results page.
 * Handles URL-driven search state, fetching, filtering, sorting, and layout.
 * The actual result card rendering is delegated to `renderCard(item)`.
 */
const ResultsPageLayout = ({ moduleKey, renderCard, emptyHint }) => {
  const baseSchema = SEARCH_MODULES[moduleKey];
  // Inject 15-category hotel filters for hotels module (MMT parity).
  const schema = useMemo(() => (
    moduleKey === 'hotels' ? { ...baseSchema, filters: HOTEL_FILTERS } : baseSchema
  ), [baseSchema, moduleKey]);
  const [searchParams, setSearchParams] = useSearchParams();

  // Pull search values from URL (or defaults).
  const searchValues = useMemo(() => {
    const obj = {};
    schema.fields.forEach((f) => {
      const url = searchParams.get(f.name);
      if (url != null) {
        obj[f.name] = url;
      } else {
        obj[f.name] = typeof f.defaultValue === 'function' ? f.defaultValue() : f.defaultValue ?? '';
      }
    });
    return obj;
  }, [schema, searchParams]);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterValues, setFilterValues] = useState({});
  const [sortValue, setSortValue] = useState(schema.sortOptions[0]?.value);
  const [mobileFilters, setMobileFilters] = useState(false);

  // Fetch when URL search params change
  useEffect(() => {
    let cancelled = false;
    // Hotels: don't fetch until user provides a city.
    if (moduleKey === 'hotels' && !String(searchValues.city || '').trim()) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const run = async () => {
      try {
        let resp;
        if (schema.method === 'POST') {
          resp = await api.post(schema.endpoint, schema.buildPayload(searchValues));
        } else {
          resp = await api.get(schema.endpoint);
        }
        if (cancelled) return;
        const list = schema.extractResults(resp.data) || [];
        setResults(list);
        // Funnel event: results rendered. Fire via posthog directly to avoid babel-plugin issue.
        try { if (window.posthog?.capture) window.posthog.capture('search_results_viewed', { module: moduleKey, count: list.length }); } catch { /* */ }
      } catch (e) {
        if (!cancelled) {
          setResults([]);
          setError('Failed to load results. Please try modifying your search.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, [searchValues, schema]);

  // Reset filters whenever the underlying results set changes
  useEffect(() => { setFilterValues({}); }, [results]);

  // Apply filters + sort
  const filtered = useMemo(() => {
    let arr = results;
    schema.filters.forEach((f) => {
      const v = filterValues[f.name];
      const isEmpty = v == null || (Array.isArray(v) && v.length === 0) || v === false;
      if (!isEmpty) {
        arr = arr.filter((r) => f.apply(r, v));
      }
      // Inline custom-range (e.g., Custom Budget under Price Per Night)
      if (f.customRange) {
        const cv = filterValues[f.customRange.name];
        if (cv && (cv.min || cv.max)) {
          arr = arr.filter((r) => f.customRange.apply(r, cv));
        }
      }
    });
    if (sortValue && schema.sortFns[sortValue]) {
      arr = [...arr].sort(schema.sortFns[sortValue]);
    }
    return arr;
  }, [results, filterValues, sortValue, schema]);

  // Number of nights between check-in & check-out (default 1 if invalid)
  const nights = useMemo(() => {
    if (moduleKey !== 'hotels') return 1;
    const ci = new Date(searchValues.check_in);
    const co = new Date(searchValues.check_out);
    if (Number.isNaN(ci.getTime()) || Number.isNaN(co.getTime())) return 1;
    return Math.max(1, Math.round((co - ci) / (1000 * 60 * 60 * 24)));
  }, [moduleKey, searchValues.check_in, searchValues.check_out]);

  const preference = filterValues.preference || 'per_night';

  const onSearch = (next) => {
    const newParams = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => {
      if (v != null && v !== '') newParams.set(k, String(v));
    });
    setSearchParams(newParams);
  };

  const isHotels = moduleKey === 'hotels';

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 transition-colors duration-200">
      {/* MMT-style scroll: the navbar above is in normal document flow (`relative`) and scrolls
          away naturally with the page. This search bar is `sticky top-0` so once the navbar
          is out of view, the search bar locks at the top — exactly like MMT. */}
      <div className="sticky top-0 z-30 bg-white dark:bg-gray-900 shadow-md">
        <div className="py-1.5">
          {isHotels ? (
            <HotelModifySearch values={searchValues} onSearch={onSearch} compact />
          ) : moduleKey === 'flights' ? (
            <FlightModifySearch values={searchValues} onSearch={onSearch} />
          ) : (
            <SearchHeader
              moduleKey={moduleKey}
              schema={schema}
              searchValues={searchValues}
              onSearch={onSearch}
            />
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)] gap-6 items-start">
          {/* Desktop sidebar — map first for hotels, then filters. Independent scroll. */}
          <div className="hidden lg:block sticky top-16 self-start max-h-[calc(100vh-5rem)] overflow-y-auto pr-1 space-y-4" data-testid="left-sidebar-scroll">
            {isHotels && (
              <HotelMapSidebar hotels={results} city={searchValues.city} />
            )}
            <FilterSidebar
              schema={schema}
              results={results}
              values={filterValues}
              onChange={setFilterValues}
              embedded
            />
          </div>

          {/* Mobile drawer — slides from left, sticky apply/clear footer */}
          {mobileFilters && (
            <div className="lg:hidden fixed inset-0 z-50 bg-black/50" onClick={() => setMobileFilters(false)} data-testid="mobile-filter-backdrop">
              <div
                className="absolute top-0 left-0 bottom-0 w-80 max-w-[85vw] bg-white dark:bg-gray-950 flex flex-col"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex justify-between items-center px-4 py-3 border-b border-gray-200 dark:border-gray-800 shrink-0">
                  <h3 className="font-bold text-gray-900 dark:text-white">Filters</h3>
                  <button onClick={() => setMobileFilters(false)} data-testid="close-mobile-filters" className="tap-target">
                    <X className="h-5 w-5 text-gray-500" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto px-4">
                  <FilterSidebar
                    schema={schema}
                    results={results}
                    values={filterValues}
                    onChange={setFilterValues}
                  />
                </div>
                <div className="border-t border-gray-200 dark:border-gray-800 p-3 grid grid-cols-2 gap-2 shrink-0 sticky-bottom-safe">
                  <button
                    type="button"
                    onClick={() => setFilterValues({})}
                    data-testid="mobile-filter-clear"
                    className="tap-target h-11 rounded-lg border border-gray-300 dark:border-gray-700 text-sm font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-900"
                  >
                    Clear all
                  </button>
                  <button
                    type="button"
                    onClick={() => setMobileFilters(false)}
                    data-testid="mobile-filter-apply"
                    className="tap-target h-11 rounded-lg bg-red-600 hover:bg-red-700 text-sm font-bold text-white"
                  >
                    Show {filtered.length} results
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Main column */}
          <div className="space-y-4 min-w-0">
            <SortBar
              count={filtered.length}
              sortValue={sortValue}
              sortOptions={schema.sortOptions}
              onSortChange={setSortValue}
              onOpenFilters={() => setMobileFilters(true)}
            />

            {loading ? (
              <ResultsSkeleton count={4} variant={isHotels ? 'hotel' : 'generic'} />
            ) : error ? (
              <div className="bg-white dark:bg-gray-900 border border-red-200 dark:border-red-800 rounded-xl p-10 text-center text-red-600 dark:text-red-300">
                {error}
              </div>
            ) : isHotels && !String(searchValues.city || '').trim() ? (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-10 text-center" data-testid="empty-city-state">
                <p className="text-gray-700 dark:text-gray-200 font-semibold mb-1">Enter a city, area or property to begin</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Use the search bar above to look up hotels worldwide.
                </p>
              </div>
            ) : filtered.length === 0 ? (
              <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl p-10 text-center" data-testid="empty-state">
                <p className="text-gray-700 dark:text-gray-200 font-semibold mb-1">No results match your filters</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {emptyHint || 'Try clearing filters or modifying your search.'}
                </p>
              </div>
            ) : (
              <div className="space-y-8">{filtered.map((item) => (
                isHotels ? renderCard({ ...item, __preference: preference, __nights: nights }) : renderCard(item)
              ))}</div>
            )}
          </div>
        </div>
        {isHotels && <RecentlyViewedHotels variant="search" className="mt-6" />}
      </div>
    </div>
  );
};

export default ResultsPageLayout;
